/* ═══════════════════════════════════════
   AirLog Constants
   All config values and mappings live here
   ═══════════════════════════════════════ */

   export const FILE_TYPE_MAP = {
    pdf:    { label: 'PDF',        letter: 'Pdf', bg: '#ad1212'},
    doc:    { label: 'Word',       letter: 'W', bg: '#006eff' },
    docx:   { label: 'Word',       letter: 'W', bg: '#006eff' },
    xls:    { label: 'Excel',      letter: 'X', bg: '#4d9654' },
    xlsx:   { label: 'Excel',      letter: 'X', bg: '#4d9654' },
    csv:    { label: 'Excel',      letter: 'X', bg: '#2E7D32' },
    ppt:    { label: 'PPT',        letter: 'P', bg: '#ad1212' },
    pptx:   { label: 'PPT',        letter: 'P', bg: '#E65100' },
    psd:    { label: 'Photoshop',  letter: 'P', bg: '#00B0FF' },
    ai:     { label: 'Illustrator',letter: 'A', bg: '#FF9800' },
    sketch: { label: 'Sketch',     letter: 'S', bg: '#F9A825' },
    fig:    { label: 'Figma',      letter: 'F', bg: '#A259FF' },
    sldprt: { label: 'SolidWorks', letter: 'S', bg: '#3E3E3E' },
    sldasm: { label: 'SolidWorks', letter: 'S', bg: '#3E3E3E' },
    dwg:    { label: 'AutoCAD',    letter: 'A', bg: '#1A1A1A' },
    dxf:    { label: 'AutoCAD',    letter: 'A', bg: '#1A1A1A' },
    logicx: { label: 'Logic Pro',  letter: 'L', bg: '#1C1C1E' },
    als:    { label: 'Ableton',    letter: 'A', bg: '#222'    },
    zip:    { label: 'ZIP',        letter: 'Z', bg: '#546E7A' },
    rar:    { label: 'RAR',        letter: 'R', bg: '#546E7A' },
    js:     { label: 'JavaScript', letter: 'J', bg: '#F7DF1E' },
    ts:     { label: 'TypeScript', letter: 'T', bg: '#3178C6' },
    py:     { label: 'Python',     letter: 'P', bg: '#3776AB' },
  }

  export const TEXT_EXTENSIONS = [
    'txt', 'md', 'json', 'csv', 'js', 'ts', 'py', 'html', 'css',
    'xml', 'yaml', 'yml', 'log', 'sh', 'bat', 'ini', 'cfg', 'env'
  ]

  export const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']

  export const EXCEL_EXTENSIONS = ['xls', 'xlsx', 'csv']

  /* ───────────────────────────────────────────────────────────────
   *  LANGUAGE_MAP — colors and metadata for code-log languages.
   *
   *  Why these specific colors?
   *  We start from the official GitHub Linguist palette so each
   *  language reads "correctly" (Swift orange, Python blue, Go cyan,
   *  Rust dark orange). Then we knock the brightness down so the
   *  colored strip on a code-log card sits in Apple Calendar's
   *  dark-mode tonal range — saturated but never neon, the way
   *  Apple's event-category swatches look on iOS dark mode.
   *
   *  Each entry:
   *    id:       internal key, matches the `language` field stored on a file
   *    label:    what we show in the chip ("JAVA SCRIPT")
   *    color:    the toned-down strip + chip color (Apple-Calendar-darkmode tone)
   *    textOn:   color of TEXT placed on top of `color`
   *              ('dark' or 'light' — used inside CodeLogCard's strip)
   *    aliases:  filename extensions that auto-map to this language
   *              when the user pastes/browses (auto-detect)
   *
   *  Adjust freely — every screen reads from this single source.
   * ─────────────────────────────────────────────────────────────── */
  export const LANGUAGE_MAP = {
    javascript: {
      id: 'javascript',
      label: 'JavaScript',
      color: '#504100',
      textOn: 'dark',
      titleColor: '#ffe711',   // feed card folder title   // feed card folder title
      captionColor: '#b9a937',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['js', 'jsx', 'mjs', 'cjs'],
    },
    typescript: {
      id: 'typescript',
      label: 'TypeScript',
      color: '#002e50',
      textOn: 'light',
      titleColor: '#007bff',   // feed card folder title   // feed card folder title
      captionColor: '#0062c8',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['ts', 'tsx'],
    },
    python: {
      id: 'python',
      label: 'Python',
      color: '#54007b',
      textOn: 'light',
      titleColor: '#c111ff',   // feed card folder title   // feed card folder title
      captionColor: '#8f37b9',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['py', 'pyw'],
    },
    swift: {
      id: 'swift',
      label: 'Swift',
      color: '#764000',
      textOn: 'light',
      titleColor: '#ff7c11',   // feed card folder title   // feed card folder title
      captionColor: '#be6b28',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['swift'],
    },
    html: {
      id: 'html',
      label: 'HTML',
      color: '#764000',
      textOn: 'light',
      titleColor: '#ff7c11',   // feed card folder title   // feed card folder title
      captionColor: '#be6b28',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['html', 'htm'],
    },
    css: {
      id: 'css',
      label: 'CSS',
      color: '#002e50',
      textOn: 'light',
      titleColor: '#007bff',   // feed card folder title   // feed card folder title
      captionColor: '#0062c8',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['css', 'scss', 'sass', 'less'],
    },
    json: {
      id: 'json',
      label: 'JSON',
      color: '#504100',
      textOn: 'light',
      titleColor: '#ffe711',   // feed card folder title   // feed card folder title
      captionColor: '#b9a937',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['json', 'jsonc'],
    },
    java: {
      id: 'java',
      label: 'Java',
      color: '#504100',
      textOn: 'light',
      titleColor: '#ffe711',   // feed card folder title   // feed card folder title
      captionColor: '#b9a937',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['java'],
    },
    c: {
      id: 'c',
      label: 'C',
      color: '#002e50',
      textOn: 'light',
      titleColor: '#007bff',   // feed card folder title   // feed card folder title
      captionColor: '#0062c8',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['c', 'h'],
    },
    cpp: {
      id: 'cpp',
      label: 'C++',
      color: '#002e50',
      textOn: 'light',
      titleColor: '#007bff',   // feed card folder title   // feed card folder title
      captionColor: '#0062c8',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['cpp', 'cxx', 'cc', 'hpp', 'hxx'],
    },
    csharp: {
      id: 'csharp',
      label: 'C#',
      color: '#54007b',
      textOn: 'light',
      titleColor: '#c111ff',   // feed card folder title   // feed card folder title
      captionColor: '#8f37b9',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['cs'],
    },
    go: {
      id: 'go',
      label: 'Go',
      color: '#00383d',
      textOn: 'light',
      titleColor: '#11ffff',   // feed card folder title   // feed card folder title
      captionColor: '#2accd1',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['go'],
    },
    rust: {
      id: 'rust',
      label: 'Rust',
      color: '#764000',
      textOn: 'light',
      titleColor: '#ff7c11',   // feed card folder title   // feed card folder title
      captionColor: '#be6b28',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['rs'],
    },
    php: {
      id: 'php',
      label: 'PHP',
      color: '#272b9d',
      textOn: 'light',
      titleColor: '#92a4ff',   // feed card folder title   // feed card folder title
      captionColor: '#4a4ed6',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['php'],
    },
    ruby: {
      id: 'ruby',
      label: 'Ruby',
      color: '#540000',
      textOn: 'light',
      titleColor: '#ff1f4e',   // feed card folder title   // feed card folder title
      captionColor: '#ad1212',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['rb'],
    },
    kotlin: {
      id: 'kotlin',
      label: 'Kotlin',
      color: '#54007b',
      textOn: 'light',
      titleColor: '#ffffff',   // feed card folder title
      captionColor: 'rgba(255,255,255,0.72)',   // feed card 'N Files' caption
      aliases: ['kt', 'kts'],
    },
    bash: {
      id: 'bash',
      label: 'Bash',
      color: '#504100',
      textOn: 'light',
      titleColor: '#ffe711',   // feed card folder title   // feed card folder title
      captionColor: '#b9a937',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['sh', 'bash', 'zsh'],
    },
    sql: {
      id: 'sql',
      label: 'SQL',
      color: '#272b9d',
      textOn: 'light',
      titleColor: '#92a4ff',   // feed card folder title   // feed card folder title
      captionColor: '#4a4ed6',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['sql'],
    },
    matlab: {
      id: 'matlab',
      label: 'MATLAB',
      color: '#764000',
      textOn: 'light',
      titleColor: '#ff7c11',   // feed card folder title   // feed card folder title
      captionColor: '#be6b28',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['m'],
    },
    r: {
      id: 'r',
      label: 'R',
      color: '#002e50',
      textOn: 'light',
      titleColor: '#007bff',   // feed card folder title   // feed card folder title
      captionColor: '#0062c8',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['r'],
    },
    markdown: {
      id: 'markdown',
      label: 'Markdown',
      color: '#00383d',
      textOn: 'light',
      titleColor: '#11ffff',   // feed card folder title   // feed card folder title
      captionColor: '#2accd1',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['md', 'markdown'],
    },
    plaintext: {
      id: 'plaintext',
      label: 'Plain Text',
      color: '#002e50',
      textOn: 'light',
      titleColor: '#007bff',   // feed card folder title   // feed card folder title
      captionColor: '#0062c8',   // feed card 'N Files' caption   // feed card 'N Files' caption
      aliases: ['txt'],
    },
  }

  /* Display order in the language popover. First 7 visible
   * without scrolling — rest reachable by scrolling. */
  export const LANGUAGE_LIST = [
    'javascript', 'typescript', 'python', 'swift', 'html', 'css', 'json',
    'java', 'c', 'cpp', 'csharp', 'go', 'rust', 'php', 'ruby', 'kotlin',
    'bash', 'sql', 'matlab', 'r', 'markdown', 'plaintext',
  ]

  /* ───────────────────────────────────────────────────────────────
   *  CODE_LOG_CONFIG — sizes, limits, behavior. Adjust freely.
   * ─────────────────────────────────────────────────────────────── */
  export const CODE_LOG_CONFIG = {
    maxFilesPerFolder: 20,
    maxFileSizeBytes: 5 * 1024 * 1024,
    feedPreviewLines: 15,
    defaultLanguage: null,
    defaultFileNamePrefix: 'File',
    keyboardToolbarTokens: ['Tab', '{', '}', '(', ')', '[', ']', ';', ':', '=', '"', "'"],
  }

  /* SYNTAX_TOKEN_COLORS — our mini-highlighter's classes.
   * BLUE-ONLY scheme (no Xcode/GitHub multi-hue).
   *   keyword  → bright light blue   (function, const, if…)
   *   string   → softer sky blue     ("hello")
   *   number   → deep/steel blue      (42, 3.14)
   *   comment  → muted grey-blue      (// note)
   *   default  → off-white            (variables, identifiers)
   *   punct    → light grey           (brackets, semicolons)
   * Tune these freely — everything stays in the blue family. */
  export const SYNTAX_TOKEN_COLORS = {
    keyword: '#4DA3FF',   // bright light blue
    string:  '#7FC4FF',   // soft sky blue
    number:  '#2F93FF',   // airlog-ish blue
    comment: '#5C6B82',   // muted grey-blue
    default: '#E8EAED',   // off-white (variables)
    punct:   '#9AA6B2',   // light grey
  }